import { useMemo, useState } from "react"

import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"

import {
  filterTree,
  indexTree,
  isCovered,
  nextSelectionAfterToggle,
  selectionState,
  type TargetingSelectionItem,
  type TargetingTreeNode,
} from "./TargetingTreeSelect.utils"

export type {
  TargetingSelectionItem,
  TargetingTreeNode,
} from "./TargetingTreeSelect.utils"

interface Props {
  tree: TargetingTreeNode[]
  selected: string[]
  /** Preferred: replace the whole selection for this tree (supports parent→child cascade). */
  onSelectionChange?: (items: TargetingSelectionItem[]) => void
  /** Legacy single-id toggle; used when onSelectionChange is omitted. */
  onToggle?: (id: string, name: string) => void
  search?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  emptySelectionText?: string
  labels?: Record<string, string>
  maxHeightClass?: string
  /** When false, hide the Selected chips block (parent can show a shared summary). */
  showSelectionSummary?: boolean
}

function TreeNodeRow({
  node,
  depth,
  selected,
  parentOf,
  expanded,
  onToggleExpand,
  onToggle,
}: {
  node: TargetingTreeNode
  depth: number
  selected: Set<string>
  parentOf: Map<string, string>
  expanded: Set<string>
  onToggleExpand: (id: string) => void
  onToggle: (node: TargetingTreeNode) => void
}) {
  const kids = node.children ?? []
  const hasKids = kids.length > 0
  const isOpen = expanded.has(node.id)
  const state = selectionState(node, selected, parentOf)
  const covered = isCovered(node.id, selected, parentOf)

  return (
    <div>
      <div
        className={
          covered || state === "some"
            ? "flex items-center gap-1 rounded bg-[#3a4060] px-1 py-1"
            : "flex items-center gap-1 rounded px-1 py-1 hover:bg-[#333]"
        }
        style={{ paddingLeft: 4 + depth * 14 }}
      >
        {hasKids ? (
          <button
            type="button"
            className="h-5 w-5 shrink-0 text-xs text-white/80"
            aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
            onClick={() => onToggleExpand(node.id)}
          >
            {isOpen ? "▾" : "▸"}
          </button>
        ) : (
          <span className="inline-block h-5 w-5 shrink-0" />
        )}
        <Checkbox
          checked={
            state === "all" ? true : state === "some" ? "indeterminate" : false
          }
          onCheckedChange={() => onToggle(node)}
          aria-label={node.name}
        />
        <button
          type="button"
          className="min-w-0 flex-1 truncate text-left text-sm text-white"
          onClick={() => onToggle(node)}
        >
          {node.name}
        </button>
      </div>
      {hasKids && isOpen
        ? kids.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selected={selected}
              parentOf={parentOf}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onToggle={onToggle}
            />
          ))
        : null}
    </div>
  )
}

export function TargetingTreeSelect({
  tree,
  selected,
  onSelectionChange,
  onToggle,
  search,
  onSearchChange,
  searchPlaceholder,
  emptySelectionText,
  labels = {},
  maxHeightClass = "max-h-72",
  showSelectionSummary = true,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const { parentOf } = useMemo(() => indexTree(tree), [tree])

  const visibleTree = useMemo(
    () => filterTree(tree, search ?? ""),
    [tree, search],
  )

  // Auto-expand matches while searching.
  const searchExpanded = useMemo(() => {
    const q = (search ?? "").trim()
    if (!q) return expanded
    const next = new Set(expanded)
    const walk = (nodes: TargetingTreeNode[]) => {
      for (const n of nodes) {
        if ((n.children ?? []).length > 0) next.add(n.id)
        walk(n.children ?? [])
      }
    }
    walk(visibleTree)
    return next
  }, [search, visibleTree, expanded])

  const selectedChips = selected.map((id) => ({
    id,
    name: labels[id] ?? id,
  }))

  const applyToggle = (node: TargetingTreeNode) => {
    if (onSelectionChange) {
      onSelectionChange(
        nextSelectionAfterToggle(node, tree, selected, labels),
      )
      return
    }
    onToggle?.(node.id, node.name)
  }

  const removeChip = (id: string, name: string) => {
    if (onSelectionChange) {
      onSelectionChange(
        selected
          .filter((x) => x !== id)
          .map((x) => ({ id: x, name: labels[x] ?? x })),
      )
      return
    }
    onToggle?.(id, name)
  }

  return (
    <div>
      {onSearchChange ? (
        <Input
          type="text"
          className="mb-2"
          placeholder={searchPlaceholder ?? "Search..."}
          value={search ?? ""}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      ) : null}

      {showSelectionSummary ? (
        <div className="mb-2 text-sm text-white">
          {selectedChips.length > 0 ? (
            <>
              <span className="font-semibold text-white">Selected:</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedChips.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => removeChip(opt.id, opt.name)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#8fa0ef]/45 bg-[#3a4060] px-2 py-0.5 text-xs text-white hover:bg-[#4a5380]"
                    aria-label={`Remove ${opt.name}`}
                  >
                    {opt.name}
                    <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <span className="text-white/70">
              {emptySelectionText ?? "None selected"}
            </span>
          )}
        </div>
      ) : null}

      <div
        className={`${maxHeightClass} overflow-y-auto rounded border border-[#5a5a5a] bg-[#1f1f1f] p-1`}
      >
        {visibleTree.length === 0 ? (
          <p className="px-2 py-3 text-xs text-white/60">No categories found</p>
        ) : (
          visibleTree.map((node) => (
            <TreeNodeRow
              key={node.id}
              node={node}
              depth={0}
              selected={selectedSet}
              parentOf={parentOf}
              expanded={searchExpanded}
              onToggleExpand={(id) => {
                setExpanded((prev) => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                })
              }}
              onToggle={applyToggle}
            />
          ))
        )}
      </div>
    </div>
  )
}
